import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { LogIn } from 'lucide-react'
import { useState } from 'react'
import { authKeys, devLoginUsers, fetchMe, localLogin, useAuthMode } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { BrandLogo } from '@/components/layout/BrandLogo'

export function LoginPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: mode } = useAuthMode()
  const routeSearch = useSearch({ strict: false }) as { error?: string }
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(
    routeSearch.error === 'access_denied'
      ? 'Je Microsoft-account is nog niet gekoppeld aan cw-finance. Vraag de beheerder om toegang.'
      : null,
  )

  async function login(email: string) {
    setPending(email)
    setError(null)
    try {
      await localLogin(email)
      queryClient.removeQueries({ queryKey: authKeys.me })
      const me = await queryClient.fetchQuery({ queryKey: authKeys.me, queryFn: fetchMe })
      if (!me) throw new Error('no session')
      await navigate({ to: '/' })
    } catch {
      setError('Inloggen mislukt. Draait de backend op :8086?')
      setPending(null)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-8 flex items-center justify-center">
          <BrandLogo markClassName="h-8" wordmarkClassName="text-lg leading-none" />
        </div>

        <div className="rounded-card bg-surface p-6 shadow-card">
          <h1 className="text-base font-semibold text-ink">Inloggen</h1>

          {mode === 'local' ? (
            <>
              <p className="mt-1 text-sm text-ink-faint">Kies een dev-account om lokaal in te loggen.</p>

              <div className="mt-5 space-y-2">
                {devLoginUsers.map((user) => (
                  <button
                    key={user.email}
                    type="button"
                    onClick={() => login(user.email)}
                    disabled={pending !== null}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border border-hairline px-3 py-2.5 text-left transition-colors hover:bg-fill-1 disabled:opacity-50',
                      pending === user.email && 'border-accent-500/40 bg-accent-600/5',
                    )}
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-600/12 text-xs font-semibold text-accent-700">
                      {user.label.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink">{user.label}</span>
                      <span className="block text-xs text-ink-faint">{user.email}</span>
                    </span>
                    <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-faint">{user.role}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm text-ink-faint">Log in met je Microsoft-account.</p>

              <a
                href="/auth/entra/login"
                className="btn-primary mt-5 flex w-full items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                Inloggen met Microsoft
              </a>
            </>
          )}

          {error ? <p className="mt-4 text-sm text-status-overdue">{error}</p> : null}
        </div>

        {mode === 'local' ? (
          <p className="mt-4 text-center text-xs text-ink-faint">Local-login is alleen beschikbaar in dev (AUTH_MODE=local).</p>
        ) : null}
      </div>
    </div>
  )
}
